import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { getUser, setAuditState } from "@/lib/storage";
import { EXPOSURE_META, fmtDate } from "@/lib/exposure";
import { BrandHeader } from "@/components/BrandHeader";
import { LoadingScreen } from "@/components/LoadingScreen";

const QUESTIONS = [
  { id: "brand_name", type: "text", question: "What's the exact brand name you want me to audit?", helper: "Type it exactly how customers see it — capitalization, spacing, the works. I'll reference this throughout your Action Plan.", placeholder: "e.g., Wildroot Skincare" },
  { id: "what_you_do", type: "textarea", question: "If a stranger asked what you do, what would you actually say?", helper: "Your real elevator pitch — not your marketing copy. The version you'd say at a dinner party.", placeholder: "We make small-batch skincare for people with sensitive skin who've given up on the drugstore aisle." },
  { id: "time_using", type: "single", question: "How long have you been using this name publicly?", options: ["Less than 6 months", "6 months to 2 years", "2 to 5 years", "5 to 10 years", "More than 10 years", "Haven't launched yet — still in planning"] },
  { id: "operations", type: "multi", question: "Where do you operate?", helper: "Select all that apply.", options: ["My local city or state (in-person clients/customers)", "Nationally (USA)", "Internationally", "Online only", "Mostly online with some in-person"] },
  { id: "registration_status", type: "single", question: "Do you have a federally registered trademark for this name?", options: ["Yes, it's officially registered (I have an ®)", "I filed an application but it's not registered yet", "No, but I've been using ™ next to my name", "No, nothing official — I've just been using it", "I'm honestly not sure"] },
  { id: "search_status", type: "single", question: "Have you ever searched the USPTO database to see if anyone else has trademarked your name or something similar?", options: ["Yes, I did a thorough search", "Yes, but it was a quick check", "No, I haven't searched", "I tried but didn't really know what I was looking for"] },
  { id: "incidents", type: "multi", question: "Has anything below ever happened to you?", helper: "Select all that apply.", options: ["I received a cease-and-desist letter or threat about my name", "I discovered someone else using a name similar to mine", "I had to change my brand name at some point", "I've worried about copycats or competitors stealing my work", "None of the above — smooth sailing so far"] },
  { id: "biggest_worry", type: "textarea", question: "Describe the specific scenario that would hurt the most if it happened.", helper: "Be honest and specific. 'Someone tries to take my brand' is fine. 'A bigger brand launches a podcast with my exact podcast name in 2027' is even better — that's the kind of detail that makes my Action Plan actually useful.", placeholder: "I'd be crushed if I built my podcast for two more years and then someone with money trademarked my exact name and forced me to rebrand right when I'm hitting my stride." },
];

// Modes: 'intro' | 'question' | 'loading' | 'report' | 'past'

export const Audit = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState("intro");
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [draft, setDraft] = useState("");
  const [draftMulti, setDraftMulti] = useState([]);
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [pastReport, setPastReport] = useState(null);
  const [pastBrand, setPastBrand] = useState(null);
  const [pastDate, setPastDate] = useState(null);

  useEffect(() => {
    if (!getUser()) { navigate("/", { replace: true }); return; }
    refreshHistory();
  }, [navigate]);

  const refreshHistory = async () => {
    try {
      const res = await api.get("/audit/history");
      setHistory(res.data?.history || []);
    } catch { /* enhancement, not core */ }
  };

  const q = QUESTIONS[idx];
  const total = QUESTIONS.length;

  const start = () => { setMode("question"); setIdx(0); setAnswers({}); setDraft(""); setDraftMulti([]); };

  const viewPast = async (id) => {
    try {
      const res = await api.get(`/audit/${id}`);
      setPastReport(res.data.report);
      setPastBrand((res.data.answers || {}).brand_name);
      setPastDate(res.data.created_at);
      setMode("past");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Couldn't load that past Action Plan. Try again.");
    }
  };

  const valid = () => {
    if (q.type === "text" || q.type === "textarea") return draft.trim().length > 0;
    if (q.type === "single") return draft !== "";
    if (q.type === "multi") return draftMulti.length > 0;
    return false;
  };

  const next = async () => {
    if (!valid()) return;
    const value = q.type === "multi" ? draftMulti : draft;
    const newAnswers = { ...answers, [q.id]: value };
    setAnswers(newAnswers);

    if (idx + 1 >= total) {
      setMode("loading");
      try {
        const payload = {
          brand_name: newAnswers.brand_name,
          what_you_do: newAnswers.what_you_do,
          time_using: newAnswers.time_using,
          operations: newAnswers.operations || [],
          registration_status: newAnswers.registration_status,
          search_status: newAnswers.search_status,
          incidents: newAnswers.incidents || [],
          biggest_worry: newAnswers.biggest_worry,
        };
        const res = await api.post(`/audit/report`, payload);
        setReport(res.data.report);
        setAuditState({ completed: true, brandName: newAnswers.brand_name, completedAt: new Date().toISOString() });
        setMode("report");
        refreshHistory();
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't generate report. Please try again.");
        setMode("question");
      }
    } else {
      setIdx(idx + 1);
      setDraft("");
      setDraftMulti([]);
    }
  };

  const back = () => {
    if (idx === 0) { setMode("intro"); return; }
    const prevIdx = idx - 1;
    const prevQ = QUESTIONS[prevIdx];
    setIdx(prevIdx);
    const prev = answers[prevQ.id];
    if (prevQ.type === "multi") { setDraftMulti(prev || []); setDraft(""); }
    else { setDraft(prev || ""); setDraftMulti([]); }
  };

  const progress = Math.round(((idx) / total) * 100);

  return (
    <div data-testid="audit-page">
      <BrandHeader />

      {mode === "intro" && <AuditIntro onStart={start} history={history} onViewPast={viewPast} />}

      {mode === "question" && (
        <section style={{ padding: "2rem 0 4rem" }}>
          <div className="container-narrow">
            <p className="eyebrow" data-testid="audit-progress-label">Question {idx + 1} of {total}</p>
            <div className="progress-bar" style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}>
              <span style={{ width: `${progress}%` }} />
            </div>

            <h2 style={{ fontSize: "clamp(1.3rem, 3.5vw, 1.7rem)", margin: "0 0 0.6rem", lineHeight: 1.25 }} data-testid="audit-question-text">
              {q.question}
            </h2>
            {q.helper && <p style={{ color: "var(--cobalt)", fontSize: "0.92rem", margin: "0 0 1.25rem" }}>{q.helper}</p>}

            {q.type === "text" && (
              <input type="text" className="brand-input" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={q.placeholder} data-testid={`audit-input-${q.id}`} autoFocus />
            )}
            {q.type === "textarea" && (
              <textarea className="brand-input" rows={5} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={q.placeholder} data-testid={`audit-input-${q.id}`} style={{ resize: "vertical", fontFamily: "Arial, sans-serif" }} autoFocus />
            )}
            {q.type === "single" && (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                {q.options.map((opt, i) => (
                  <button key={opt} type="button" className={`option-btn ${draft === opt ? "selected" : ""}`} onClick={() => setDraft(opt)} data-testid={`audit-option-${q.id}-${i}`}>
                    <span className="option-letter">{String.fromCharCode(65 + i)}</span>
                    <span>{opt}</span>
                  </button>
                ))}
              </div>
            )}
            {q.type === "multi" && (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                {q.options.map((opt, i) => {
                  const checked = draftMulti.includes(opt);
                  return (
                    <button key={opt} type="button" className={`option-btn ${checked ? "selected" : ""}`} onClick={() => setDraftMulti(prev => checked ? prev.filter(x => x !== opt) : [...prev, opt])} data-testid={`audit-option-${q.id}-${i}`}>
                      <span className="option-letter" style={{ background: checked ? "var(--navy)" : "transparent", color: checked ? "var(--warm-white)" : "var(--navy)" }}>
                        {checked ? "✓" : String.fromCharCode(65 + i)}
                      </span>
                      <span>{opt}</span>
                    </button>
                  );
                })}
              </div>
            )}

            <div style={{ marginTop: "1.5rem", display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
              <button type="button" className="btn-yellow" onClick={next} disabled={!valid()} data-testid="audit-next-button">
                {idx + 1 >= total ? "Build my Action Plan →" : "Next →"}
              </button>
              <button type="button" className="btn-outline-navy" onClick={back} data-testid="audit-back-button">
                ← Back
              </button>
            </div>
          </div>
        </section>
      )}

      {mode === "loading" && <LoadingScreen brandName={answers.brand_name} />}

      {mode === "report" && report && (
        <AuditReport report={report} brandName={answers.brand_name} dateLabel={fmtDate(new Date().toISOString())} isPast={false} onRetake={start} />
      )}

      {mode === "past" && pastReport && (
        <AuditReport
          report={pastReport}
          brandName={pastBrand}
          dateLabel={fmtDate(pastDate)}
          isPast={true}
          onRetake={start}
          onBackToIntro={() => { setMode("intro"); setPastReport(null); setPastBrand(null); setPastDate(null); }}
        />
      )}
    </div>
  );
};

const AuditIntro = ({ onStart, history, onViewPast }) => {
  const hasHistory = history && history.length > 0;
  return (
    <section style={{ padding: "3rem 0 4rem" }}>
      <div className="container-narrow">
        <p className="eyebrow">Get personal</p>
        <h1 style={{ fontSize: "clamp(2rem, 5vw, 2.6rem)", margin: "0.3rem 0 1.2rem", letterSpacing: "-0.02em" }} data-testid="audit-intro-title">
          Your Brand Protection Audit
        </h1>
        <div style={{ fontSize: "1.05rem", lineHeight: 1.65 }}>
          <p>{hasHistory ? "Welcome back." : "This is the deeper read."}</p>
          <p>The Legal IQ Assessment gives you the snapshot. The Audit puts your specific brand under the microscope. I'll ask 8 real questions — the kind I'd ask a new client on a discovery call — and the more specific you are, the sharper the Action Plan I can build you.</p>
          <p>At the end, you'll walk away with:</p>
          <ul style={{ paddingLeft: "1.2rem" }}>
            <li>An exposure read on <em>your</em> specific brand</li>
            <li>What's working in your favor, what's leaving you exposed</li>
            <li>A 3-move Action Plan you can start this month</li>
          </ul>
          <p style={{ color: "var(--cobalt)", fontWeight: 700 }}>
            When to take this: anytime something material changes — a new product, a new market, a copycat sighting, or a name you're about to commit to.
          </p>
        </div>
        <button type="button" className="btn-yellow" onClick={onStart} data-testid="audit-start-button" style={{ marginTop: "1.5rem" }}>
          {hasHistory ? "Run a new Audit →" : "Let's do this →"}
        </button>

        {hasHistory && (
          <div style={{ marginTop: "3rem" }} data-testid="audit-history-section">
            <h2 style={{ fontSize: "1.05rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--navy)", borderBottom: "2px solid var(--yellow)", paddingBottom: "0.4rem", margin: "0 0 1.1rem" }}>
              Your past Action Plans
            </h2>
            <p style={{ margin: "0 0 1rem", color: "var(--midnight)" }}>
              Click any Audit below to re-read the Action Plan I built for you.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.7rem" }}>
              {history.map((h) => {
                const meta = EXPOSURE_META[h.exposure_level] || EXPOSURE_META.medium;
                return (
                  <button
                    key={h.id}
                    type="button"
                    onClick={() => onViewPast(h.id)}
                    data-testid={`audit-history-item-${h.id}`}
                    style={{
                      textAlign: "left",
                      background: "var(--warm-white)",
                      border: "2px solid var(--navy)",
                      borderLeft: `8px solid ${meta.color}`,
                      padding: "0.9rem 1rem",
                      cursor: "pointer",
                      font: "inherit",
                      color: "inherit",
                      boxShadow: "3px 3px 0 0 var(--navy)",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.3rem", flexWrap: "wrap" }}>
                      <span style={{ fontSize: "0.72rem", fontWeight: 700, padding: "0.2rem 0.5rem", background: meta.color, color: "var(--warm-white)", letterSpacing: "0.05em" }}>
                        {meta.label} EXPOSURE
                      </span>
                      <span style={{ color: "var(--cobalt)", fontWeight: 700, fontSize: "0.85rem" }}>
                        {fmtDate(h.created_at)}
                      </span>
                      {h.brand_name && (
                        <span style={{ color: "var(--midnight)", fontSize: "0.9rem" }}>
                          · <strong>{h.brand_name}</strong>
                        </span>
                      )}
                    </div>
                    {h.headline && (
                      <div style={{ color: "var(--midnight)", fontSize: "0.95rem", lineHeight: 1.45 }}>
                        {h.headline}
                      </div>
                    )}
                    <div style={{ marginTop: "0.4rem", color: "var(--cobalt)", fontWeight: 700, fontSize: "0.85rem" }}>
                      View this Action Plan →
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const AuditReport = ({ report, brandName, dateLabel, isPast, onRetake, onBackToIntro }) => (
  <section style={{ padding: "2.5rem 0 4rem" }} data-testid="audit-report">
    <div className="container-narrow">
      {isPast && (
        <div className="callout" style={{ marginBottom: "1.5rem", borderTopColor: "var(--cobalt)" }} data-testid="audit-viewing-past-banner">
          <p style={{ margin: 0 }}>
            <strong>Viewing your Action Plan from {dateLabel}.</strong>{" "}
            {onBackToIntro && (
              <button type="button" onClick={onBackToIntro} style={{ background: "none", border: "none", padding: 0, color: "var(--cobalt)", fontWeight: 700, cursor: "pointer", textDecoration: "underline" }} data-testid="audit-back-to-intro">
                ← Back to all
              </button>
            )}
          </p>
        </div>
      )}

      <p className="eyebrow">Your Brand Protection Audit · {brandName}</p>
      <h1 style={{ fontSize: "clamp(1.7rem, 4.5vw, 2.3rem)", margin: "0.3rem 0 1.5rem", letterSpacing: "-0.01em", lineHeight: 1.2 }} data-testid="audit-report-headline">
        {report.headline}
      </h1>

      <Section title="What's working in your favor">
        <ul style={{ paddingLeft: "1.2rem", margin: 0 }} data-testid="audit-report-favor">
          {report.working_in_favor?.map((b, i) => <li key={i} style={{ marginBottom: "0.5rem" }}>{b}</li>)}
        </ul>
      </Section>

      <Section title="Where you're exposed">
        <ul style={{ paddingLeft: "1.2rem", margin: 0 }} data-testid="audit-report-exposed">
          {report.where_exposed?.map((b, i) => <li key={i} style={{ marginBottom: "0.5rem" }}>{b}</li>)}
        </ul>
      </Section>

      <Section title="What this means in plain English">
        <p style={{ margin: 0 }} data-testid="audit-report-plain">{report.plain_english}</p>
      </Section>

      <Section title="Your Action Plan — 3 moves, in order">
        <ol style={{ paddingLeft: "1.2rem", margin: 0 }} data-testid="audit-report-moves">
          {report.next_moves?.map((m, i) => (
            <li key={i} style={{ marginBottom: "0.9rem" }}>
              <strong>{m.title}</strong>
              <div>{m.body}</div>
            </li>
          ))}
        </ol>
      </Section>

      <Section title="The bottom line">
        <p style={{ margin: 0, whiteSpace: "pre-wrap" }} data-testid="audit-report-bottom">{report.bottom_line}</p>
      </Section>

      <div className="callout" style={{ marginTop: "1.5rem" }}>
        <p style={{ margin: 0, fontStyle: "italic" }} data-testid="audit-report-disclaimer">
          {report.disclaimer}
        </p>
      </div>

      {report.high_exposure_inline_mention && report.high_exposure_inline_mention.trim() && (
        <div className="callout" style={{ marginTop: "1.25rem", borderTopColor: "var(--evergreen)" }} data-testid="audit-high-exposure-mention">
          <p style={{ margin: 0 }}>
            <em>{report.high_exposure_inline_mention}</em>{" "}
            <a href="https://www.indielaw.com/call" target="_blank" rel="noopener noreferrer">Book a free call →</a>
          </p>
        </div>
      )}

      <div style={{ marginTop: "2.5rem", display: "flex", flexDirection: "column", gap: "1.2rem" }}>
        {isPast && (
          <div>
            <button type="button" onClick={onRetake} className="btn-yellow" data-testid="audit-retake-button">
              Run a new Audit →
            </button>
          </div>
        )}

        <div>
          <p style={{ margin: "0 0 0.5rem" }}>
            Ready to learn how to actually call dibs on <strong>{brandName}</strong>?
          </p>
          <a
            href="https://indielaw.easywebinar.live/gettrademarked"
            target="_blank" rel="noopener noreferrer"
            className={isPast ? "btn-outline-navy" : "btn-yellow"}
            data-testid="audit-webinar-cta"
          >
            Join my free training: Protect Your Brand & Get Legally Legit →
          </a>
        </div>

        <div className="divider-rule" />

        <div>
          <p style={{ margin: "0 0 0.5rem" }}>
            Want to know how strong your brand <em>name</em> itself is — not just the trademark situation?
          </p>
          <Link to="/name-test" className="btn-outline-navy" data-testid="audit-name-test-cta">
            Take the Name Evaluator →
          </Link>
        </div>

        <div className="callout">
          <p style={{ margin: 0 }}>
            Want to go deeper? My book <em>Legally Legit</em> covers all of this and more.{" "}
            <a href="https://a.co/d/0isDjKfG" target="_blank" rel="noopener noreferrer" data-testid="audit-book-link">
              Get it on Amazon →
            </a>
          </p>
        </div>

        <Link to="/dashboard" data-testid="audit-back-to-dashboard">← Back to dashboard</Link>
      </div>
    </div>
  </section>
);

const Section = ({ title, children }) => (
  <div style={{ marginBottom: "1.8rem" }}>
    <h2 style={{ fontSize: "1.15rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--navy)", borderBottom: "2px solid var(--yellow)", paddingBottom: "0.4rem", margin: "0 0 0.85rem" }}>
      {title}
    </h2>
    {children}
  </div>
);
